// @ts-strict-ignore
import {DisplayOptions, ShowClause} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
} from 'common/types/reports/bookmark';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {properties} from 'common/report/util/settings-tracking';
// eslint-disable-next-line import/no-cycle
import {checkIsMultiMetricBookmark, getDisplayOptions} from 'common/report/insights/util';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';

import {ANOMALY_DETECTION_ALLOWED_TIME_UNITS, CUSTOM_ALERT_TYPE} from './constants';
import {CustomAlert, SlackWebhookError, SlackWebookSubscription, SubscriptionType} from './types';

export function validateSlackWebhook(subscription: SlackWebookSubscription): SlackWebhookError {
  if (subscription.type !== SubscriptionType.SlackWebhook) {
    throw Error(`Incorrect subscription type`);
  }

  let error = null;
  if (!subscription.data.slack_webhook_url.startsWith(`https://hooks.slack.com/services/`)) {
    error = SlackWebhookError.InvalidUrl;
  }
  return error;
}

export function customAlertCoreTrackingProperties(customAlert: CustomAlert) {
  const {bookmarkId, id, projectId} = customAlert;
  return {[properties.projectId]: projectId, [properties.bookmarkId]: bookmarkId, [properties.customAlertId]: id};
}

export function customAlertTrackingProperties(customAlert: CustomAlert) {
  const {condition, subscriptions} = customAlert;
  const emailSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.Email);
  const webhookSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.SlackWebhook);
  const channelSubscriptions = (subscriptions || []).filter((sub) => sub.type === SubscriptionType.SlackChannel);
  const projectWebhookSubscriptions = (subscriptions || []).filter(
    (sub) => sub.type === SubscriptionType.ProjectWebhook,
  );
  return {
    ...customAlertCoreTrackingProperties(customAlert),
    'Condition Threshold': condition.value,
    'Condition Operator': condition.op,
    'Condition Type': condition.type,
    '# of Email recipients': emailSubscriptions.length,
    '# of Recipients': subscriptions.length,
    '# of Webhook Recipients': webhookSubscriptions.length,
    '# of Slack Recipients': channelSubscriptions.length,
    '# of Project Webhook Recipients': projectWebhookSubscriptions.length,
    'Has Email Recipients': !!subscriptions.length,
    'Has Webhook Recipients': !!webhookSubscriptions.length,
    'Has Slack Recipients': !!channelSubscriptions.length,
    'Has Project Webhook Recipients': !!projectWebhookSubscriptions.length,
    ...(condition.type === CUSTOM_ALERT_TYPE.anomaly
      ? {
          Confidence: condition.confidence,
          'Training Mode': condition.trainingMode,
        }
      : {}),
  };
}

export function isAnomalyDetectionVizEnabled() {
  return true;
}

export function checkConversionWindowValidityForFunnelsMetrics(bookmarkParams: any, isMultiMetricBookmark: boolean) {
  if (!isMultiMetricBookmark) {
    return true;
  }

  const behaviorClauses = getBehaviorShowClauses(bookmarkParams.sections.show);
  const {type, conversionWindowUnit, conversionWindowDuration} = behaviorClauses[0][`behavior`];
  const isFunnel = type === `funnel`;

  // Conversion Window can't be longer than 3 months for anomaly detection
  const durationMap = new Map([
    [`quarter`, 1],
    [`month`, 3],
    [`week`, 12],
    [`day`, 90],
    [`hour`, 90 * 24],
    [`minute`, 90 * 24 * 60],
    [`second`, 90 * 24 * 60 * 60],
  ]);
  const isLongDuration = durationMap.get(conversionWindowUnit) <= conversionWindowDuration;

  let isValidConversionWindow = true;
  if (isMultiMetricBookmark && isFunnel && isLongDuration) {
    isValidConversionWindow = false;
  }
  return isValidConversionWindow;
}

// keep logic in sync with backend is_bookmark_valid_for_anomaly_detection_alert
// https://github.com/mixpanel/analytics/blob/ecc9465876c6f8d14735252d170108eeed84db4d/webapp/custom_alerts/util.py#L369
export function isValidAnomalyDetectionBookmark(
  bookmarkType: BookmarkType,
  bookmarkParams: {
    sections: any;
    displayOptions?: DisplayOptions;
    timeComparison?: any;
    liftComparison?: any;
  },
) {
  const isMultiMetricBookmark = checkIsMultiMetricBookmark(bookmarkParams?.sections?.show);

  if (bookmarkType === BookmarkType.Insights || isMultiMetricBookmark) {
    const displayOptions = bookmarkParams?.displayOptions;
    const bookmarkChartType = displayOptions?.chartType;
    const bookmarkPlotStyle = displayOptions?.plotStyle;
    const bookmarkAnalysisType = displayOptions?.analysis;
    const bookmarkHasTimeComparison = !!bookmarkParams?.timeComparison;
    const bookmarkHasLiftComparison = !!bookmarkParams?.liftComparison;
    const isValidConversionWindow = checkConversionWindowValidityForFunnelsMetrics(
      bookmarkParams,
      isMultiMetricBookmark,
    );

    const isCohortOvertime = bookmarkParams?.sections?.show?.some((showElement) => {
      const resourceType = isMultiMetricBookmark ? showElement.behavior?.resourceType : showElement.resourceType;
      return resourceType === InsightsResourceType.Cohorts;
    });

    const isAllowedTimeUnit = ANOMALY_DETECTION_ALLOWED_TIME_UNITS.includes(bookmarkParams.sections.time[0]?.unit);

    const isValid = !(
      ![ChartType.Line, ChartType.Column].includes(bookmarkChartType) ||
      bookmarkPlotStyle !== ChartPlotStyle.Standard ||
      bookmarkHasLiftComparison ||
      bookmarkHasTimeComparison ||
      !isValidConversionWindow ||
      bookmarkAnalysisType === AnalysisType.Rolling ||
      bookmarkAnalysisType === AnalysisType.Cumulative ||
      isCohortOvertime ||
      !isAllowedTimeUnit
    );
    return isValid;
  }
  return false;
}

export function isCustomAlertConditionTypesEnabled(bookmarkType: BookmarkType, bookmarkParams: any) {
  return (
    bookmarkType === BookmarkType.Insights &&
    [ChartType.Line, ChartType.Column].includes(
      getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions).chartType,
    )
  );
}

// Bookmark type required because isPercentage and isPercentageThreshold is currenlty only used for funnel alerts.
// With new multimetric bookmarks and count types across both funnels and insights, it could be possible to expand this
// to insights in the future but currently we are not touching insights when it comes to percentage alert types.
// Insights alert conditions are handled differently until we put some effort into a refactor to have the two alert types converge.
export function isPercentageAlertType(bookmarkType: BookmarkType, bookmarkParams: InsightsBookmarkParams) {
  const bookmarkSections = bookmarkParams?.sections;
  const isMultiMetricBookmark = checkIsMultiMetricBookmark(bookmarkSections?.show as ShowClause[]);
  const isFunnelsBookmark = bookmarkType === BookmarkType.Funnels;
  let isPercentage = false;
  if (isMultiMetricBookmark && isFunnelsBookmark) {
    const countType = bookmarkSections?.show[0][`measurement`][`math`];
    if (
      [
        FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      ].includes(countType)
    ) {
      isPercentage = true;
    }
  }
  return isPercentage;
}
